import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Spinner, EmptyState, StatusBadge } from "@/components/common/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { inr } from "@/lib/constants";

const errMsg = (e) => formatApiErrorDetail(e?.response?.data?.detail);

// Generic org-scoped CRUD module. fields: [{key,label,type,options,optional}]
// columns: [{key,label,render}]
export function CrudModule({ orgId, path, title, subtitle, fields, columns, addLabel = "Add", testid, canEdit = true, canDelete = true }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const base = `/organizations/${orgId}/${path}`;

  const load = () => api.get(base).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (orgId) load(); /* eslint-disable-next-line */ }, [orgId, path]);

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name || row.title || "this item"}"?`)) return;
    try { await api.delete(`${base}/${row.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const blank = () => fields.reduce((a, f) => ({ ...a, [f.key]: f.type === "number" ? "" : (f.options ? f.options[0] : "") }), {});

  return (
    <div data-testid={`module-${testid}`}>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-sp-navy">{title}</h1>{subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}</div>
        <Button data-testid={`${testid}-add`} onClick={() => setEdit(blank())} className="bg-sp-orange hover:bg-sp-orangedark rounded-full px-5"><Plus className="mr-1 h-4 w-4" />{addLabel}</Button>
      </div>
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState title={`No ${title.toLowerCase()} yet`} actionLabel={addLabel} onAction={() => setEdit(blank())} /> : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
              {columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium">{c.label}</th>)}
              <th className="px-4 py-3 text-right">Actions</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`${testid}-row-${r.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  {columns.map((c) => <td key={c.key} className="px-4 py-3 text-sp-navy">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canEdit && <Button size="icon" variant="ghost" data-testid={`${testid}-edit-${r.id}`} onClick={() => setEdit({ ...r })}><Pencil className="h-4 w-4" /></Button>}
                    {canDelete && <Button size="icon" variant="ghost" data-testid={`${testid}-del-${r.id}`} onClick={() => remove(r)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <CrudDialog base={base} fields={fields} row={edit} title={title} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} testid={testid} />}
    </div>
  );
}

function CrudDialog({ base, fields, row, title, onClose, onSaved, testid }) {
  const isNew = !row.id;
  const [f, setF] = useState(row);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    const payload = { ...f };
    fields.forEach((fl) => { if (fl.type === "number") payload[fl.key] = payload[fl.key] === "" ? null : Number(payload[fl.key]); });
    try {
      if (isNew) await api.post(base, payload); else await api.patch(`${base}/${row.id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${testid}-dialog`} className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? `Add ${title}` : `Edit ${title}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {fields.map((fl) => (
            <div key={fl.key} className={fl.full ? "col-span-2 space-y-1.5" : "space-y-1.5"}>
              <Label className="text-xs text-slate-500">{fl.label}</Label>
              {fl.options ? (
                <Select value={f[fl.key] || fl.options[0]} onValueChange={(v) => set(fl.key, v)}>
                  <SelectTrigger data-testid={`${testid}-${fl.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{fl.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input data-testid={`${testid}-${fl.key}`} type={fl.type === "number" ? "number" : "text"}
                  value={f[fl.key] ?? ""} onChange={(e) => set(fl.key, e.target.value)} placeholder={fl.placeholder} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid={`${testid}-save`} onClick={save} disabled={busy} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { inr };
